'use client';

import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../_lib/utils';
import { Container } from '../Container';
import { Button, ButtonCtaAction, type ButtonCtaActionProps } from '../Inputs/Button';
import { IconResolver, type IconType } from '../IconResolver';
import type { SanityImage } from '../types';
import { Avatar } from '../Avatar';
import { Text } from '../Text';
import { useGetPageCurrentScrollContainerRef, useLenis } from '../LenisScroll';
import { useMotionValueEvent, useScroll } from 'motion/react';
import { useSetAtom } from 'jotai';
import { showHeaderAtom } from '../Header';

type SubNavSection = {
	anchorLabel?: string;
	image?: SanityImage;
	children?: ReactNode;
	chapters: SubNavChapter[];
};

type SubNavChapter = {
	anchorLabel?: string;
	icon?: IconType;
	children?: ReactNode;
};

export type SubNavProps = {
	className?: string;
	button?: ButtonCtaActionProps;
	sections?: SubNavSection[];
	backToTopText?: string;
};

export function SubNav(props: SubNavProps) {
	const baseId = useId();
	const ticking = useRef<boolean>(false);
	const refs = useRef<Map<number, HTMLDivElement>>(new Map());
	const [activeSection, setActiveSection] = useState<{ index: number; chapter: number; progress: number } | null>(null);

	const setShowHeader = useSetAtom(showHeaderAtom);
	const lenis = useLenis();
	const { scrollWrapperRef, scrollContentRef } = useGetPageCurrentScrollContainerRef();
	const { scrollY } = useScroll({ container: scrollWrapperRef, target: scrollContentRef, layoutEffect: false });

	const refCallback = (ref: HTMLDivElement | null, sectionIndex: number) => {
		if (ref === null) {
			// Cleanup
			refs.current.delete(sectionIndex);
			return;
		}

		refs.current.set(sectionIndex, ref);
	};

	const backToTopText = props.backToTopText ?? 'Back to top';

	useEffect(() => {
		// Hide Header when SubNav is active
		setShowHeader(activeSection === null ? true : false);

		return () => {
			setShowHeader(true);
		};
	}, [activeSection]);

	useMotionValueEvent(scrollY, 'change', () => {
		if (ticking.current) {
			return;
		}

		window.requestAnimationFrame(() => {
			ticking.current = false;

			// Sections are sorted in a descending order (last section is first entry)
			const entries = [...refs.current.entries()].sort((a, b) => b[0] - a[0]);

			for (const [index, el] of entries) {
				const rect = el.getBoundingClientRect();

				const startsAboveTheViewport = rect.top <= 1;
				const endsAboveTheViewport = rect.bottom <= 1;

				if (!startsAboveTheViewport || endsAboveTheViewport) {
					// Section isn't active

					if (index === 0) {
						// First section is in or below viewport
						setActiveSection(null);
					}
					continue;
				}

				const chapters = el.children;

				for (let i = chapters.length - 1; i >= 0; i--) {
					const chapter = chapters.item(i)!;
					const chapterRect = chapter.getBoundingClientRect();

					if (chapterRect.top > 1) {
						// Chapter isn't active
						continue;
					}

					const progress = Math.min(Math.abs(chapterRect.top) / chapterRect.height, 1);

					setActiveSection({ index, chapter: i, progress });

					// This chapter is already above the viewport, no point in checking previous chapter.
					break;
				}

				// This section is already above the viewport, no point in checking previous section.
				break;
			}
		});
	});

	if (props.sections === undefined || props.sections.length === 0) {
		return null;
	}

	return (
		<div className={props.className} data-component='SubNav'>
			<div
				aria-hidden={activeSection === null ? true : false}
				inert={activeSection === null ? '' : undefined}
				className={cn(
					'hidden fixed z-1 top-0 left-0 w-full bg-background-white/90 border-b border-border-secondary backdrop-blur-2xl transition-opacity duration-normal lg:block',
					activeSection === null ? 'opacity-0 delay-700 pointer-events-none' : 'opacity-100',
				)}
			>
				<Container
					className='py-4 grid grid-cols-[auto_minmax(0,1fr)_auto] gap-spacing-md items-center'
					data-theme='light'
					data-header-ignore
				>
					<Button
						className='font-medium'
						parent='SubNav'
						iconLeft={<IconResolver icon='arrow-bend-left-up' className='w-3.5 h-3.5' />}
						styleType='ghost'
						onClick={() => {
							lenis?.scrollTo(0);
						}}
					>
						{backToTopText}
					</Button>

					<div className='flex gap-spacing-md justify-center'>
						{props.sections.map((section, i) => (
							<SubNavItem
								baseId={baseId}
								sectionIndex={i}
								activeChapterIndex={activeSection?.index === i ? activeSection.chapter : null}
								progress={activeSection?.index === i ? activeSection.progress : 0}
								key={`${section.anchorLabel}-${i}-sub-nav-item`}
								label={section.anchorLabel}
								image={section.image}
								chapters={section.chapters}
							/>
						))}
					</div>

					<div>{props.button && <ButtonCtaAction {...props.button} {...props.button.buttonProps} />}</div>
				</Container>
			</div>

			<div>
				{props.sections.map((section, i) => (
					<div
						key={`${section.anchorLabel}-${i}-sub-nav-section`}
						ref={ref => {
							refCallback(ref, i);
						}}
					>
						<div id={`${baseId}-${i}-0`}>{section.children}</div>

						{section.chapters.map((chapter, j) => (
							<div key={`${chapter.anchorLabel}-${j}-sub-nav-chapter`} id={`${baseId}-${i}-${j + 1}`}>
								{chapter.children}
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	);
}

type SubNavItemProps = {
	baseId: string;
	sectionIndex: number;
	activeChapterIndex: number | null;
	progress: number;
	label?: string;
	image?: SanityImage;
	chapters: Omit<SubNavChapter, 'children'>[];
};

function SubNavItem(props: SubNavItemProps) {
	const isExpanded = props.activeChapterIndex !== null;

	const ref = useRef<HTMLDivElement>(null);
	const chapterRefs = useRef<(HTMLDivElement | null)[]>([]);

	const [scale, setScale] = useState(0);

	const lenis = useLenis();

	const scrollToChapter = (chapterIndex: number) => {
		const el = document.querySelector<HTMLElement>('#' + CSS.escape(`${props.baseId}-${props.sectionIndex}-${chapterIndex}`));

		if (el !== null) {
			lenis?.scrollTo(el);
		}
	};

	useEffect(() => {
		if (isExpanded) {
			// Super complex and inefficient way of measuring the size of an expanded item, but other methods failed :(
			const clone = ref.current!.cloneNode(true) as HTMLDivElement;
			Object.assign(clone.style, {
				position: 'absolute',
				visibility: 'hidden',
				maxWidth: 'none',
				width: 'auto',
				height: 'auto',
			});

			document.body.appendChild(clone);
			const width = clone.offsetWidth;
			document.body.removeChild(clone);

			ref.current!.style.maxWidth = `${width}px`;
		} else {
			ref.current!.style.maxWidth = '0';
		}
	}, [isExpanded]);

	useEffect(() => {
		// Calculate the scale based on chapter widths
		if (props.activeChapterIndex !== null) {
			const chapterWidths = chapterRefs.current.map(ref => ref?.offsetWidth || 0);
			const totalWidth = chapterWidths.reduce((sum, width) => sum + width, 0);

			// Calculate the cumulative width up to the active chapter
			const previousChaptersWidth = chapterWidths.slice(0, props.activeChapterIndex).reduce((sum, width) => sum + width, 0);

			// Add the progress of the current chapter
			const currentChapterWidth = chapterWidths[props.activeChapterIndex] || 0;
			const progressInCurrentChapter = currentChapterWidth * props.progress;

			// Calculate the final scale
			const newScale = (previousChaptersWidth + progressInCurrentChapter) / totalWidth;
			setScale(newScale);
		}
	}, [props.activeChapterIndex, props.progress]);

	const style = { '--scale': scale } as CSSProperties;

	return (
		<div
			className={cn(
				'shrink-0 relative isolate flex overflow-hidden rounded-2xl transition-colors duration-fast',
				'before:absolute before:-z-1 before:top-0 before:left-0 before:w-full before:h-full before:origin-left before:transition-all before:duration-200 before:ease-linear before:scale-x-(--scale)',
				isExpanded ? 'bg-background-brand-5 before:bg-background-brand-20' : 'hover:bg-background-brand-5',
			)}
			style={style}
		>
			<div
				ref={el => {
					chapterRefs.current[0] = el;
				}}
				className='flex gap-spacing-3xs items-center select-none cursor-pointer p-1 pr-spacing-xs'
				onClick={() => {
					scrollToChapter(0);
				}}
			>
				<Avatar className='ring-1 ring-white' image={props.image} size='sm' />

				<Text styleType='button-md'>{props.label}</Text>
			</div>

			<div ref={ref} className='flex transition-all duration-slow max-w-0'>
				{props.chapters.map((chapter, i) => (
					<div
						key={`${chapter.anchorLabel}-${i}-sub-nav-chapter-item`}
						ref={el => {
							chapterRefs.current[i + 1] = el;
						}}
						className='flex gap-spacing-3xs items-center select-none cursor-pointer px-spacing-xs'
						onClick={() => {
							scrollToChapter(i + 1);
						}}
					>
						<IconResolver className='min-w-3.5! min-h-3.5! w-3.5! h-3.5! shrink-0 max-w-3.5! max-h-3.5!' icon={chapter.icon} />

						<Text className='font-medium' styleType='button-md'>
							{chapter.anchorLabel}
						</Text>
					</div>
				))}
			</div>
		</div>
	);
}
